import { useCallback, useEffect, useReducer, useRef } from 'react';
import { v4 as uid } from 'uuid';
import { updateLike } from '@/pages/datalab/pages/BehaviorAnalysis/GptAnalysis/service';
import {
  MessageTypeEnum,
  ChatRequestParams,
  BackInfo,
  BackChartInfo
} from '@/pages/datalab/pages/BehaviorAnalysis/GptAnalysis/interface';
import { omit } from 'lodash-es';
import { useNextAskPromptExample } from './hooks';
import { getAuthorization } from '@/utils/utils';

// 定义状态类型
interface ChatState {
  messages: any[]; // 页面显示的消息
  isComplete: boolean;
  /**
	 * 存储回传给算法端的消息
	 * 1. 回传的消息有用户的问题和summary, text两种回答, 错误消息不回传.
	 * 2. 用户问的问题和回答都进行回传, 前端不做数量的判断
	 * 3. 用户点击清空按钮的时候, 之前的回传消息也一起清空
	 * 4. 还要将图表的标题和下载地址一起回传
	 */
  results: BackInfo[];
  // 算法端要求回传图表消息的标题和下载地址
  chartInfo: BackChartInfo[];
}

// 定义 Action 类型
type Action =
	| { type: 'FETCH_MESSAGE'; payload: any; }
	| { type: 'CANCEL_TASK'; }
	| { type: 'ADD_RIGHT_MESSAGE'; payload: { content: string; position?: 'left' | 'right'; }; }
	| { type: 'ADD_LEFT_MESSAGE'; payload: any; }
	| { type: 'CONCAT_CONTENT'; payload: any; }
	| { type: 'END_LAST_MESSAGE_STATUS'; }
	| { type: 'SAVE_CHART_INFO'; payload: { title: string; url: string; }; }
	| { type: 'TASK_STATE'; payload: boolean; }
	| { type: 'TASK_DONE'; }
	| { type: 'RESET'; };
// 定义 reducer 函数
const reducer = (state: ChatState, action: Action): ChatState => {
  try {
    switch (action.type) {
      case 'ADD_RIGHT_MESSAGE': {
        return {
          ...state,
          isComplete: false,
          messages: [
            ...state.messages,
            {
              type: 'text',
              position: 'right',
              content: action.payload.content,
              status: 'end'
            }
          ],
          results: [
            ...state.results,
            {
              role: 'user',
              content: action.payload.content
            }
          ]
        };
      }
      case 'ADD_LEFT_MESSAGE': {
        return { ...state, messages: [...state.messages, action.payload] };
      }
      case 'CONCAT_CONTENT': {
        const lastMessage = state.messages[state.messages.length - 1] || {};
        lastMessage.content += action.payload.content;
        lastMessage.status = action.payload.status;
        return { ...state };
      }
      case 'END_LAST_MESSAGE_STATUS': {
        const lastMessage = state.messages[state.messages.length - 1] || {};
        lastMessage.status = 'end';
        return { ...state };
      }
      case 'SAVE_CHART_INFO': {
        state.chartInfo.push(action.payload);
        return state;
      }
      case 'TASK_STATE': {
        return { ...state, isComplete: action.payload };
      }
      case 'TASK_DONE': {
        // 分析任务结束
        const lastMessage = state.messages[state.messages.length - 1] || {};
        lastMessage.status = 'end';
        if (['summary', 'text'].includes(lastMessage?.type)) {
          // 只有总结和普通文本的回答需要被回传给算法端.
          const message = {
            role: 'assistant',
            content: lastMessage?.content
          };
          return { ...state, isComplete: true, results: [...state.results, message] };
        }
        return { ...state, isComplete: true };
      }
      case 'RESET':
        return {
          messages: [],
          isComplete: true,
          results: [],
          chartInfo: []
        };
      default:
        return state;
    }
  } catch (e) {
    console.error('useChatReduce Error: ', e);
    return state;
  }
};

const initialState: ChatState = {
  messages: [],
  isComplete: true,
  results: [],
  chartInfo: []
};
// 判断字符是汉字或者字母
function isValidCharacter(char: string) {
  if (!char) return true;
  const code = char.charCodeAt(0);
  return (code >= 0x4e00 && code <= 0x9fff) || (code >= 0x41 && code <= 0x5a) || (code >= 0x61 && code <= 0x7a);
}

// 接收到后端返回的数据后, 前端对文本内容进行分割
const splitContent = (newMessage, dispatch, splitStatusRef, language) =>
  new Promise(resolve => {
    // splitStatusRef.current.isStreaming = true;
    splitStatusRef.current.resolve = resolve; // 在浏览器切换页签的时候可以停止Promise
    const length: number = newMessage?.content?.length;
    let position = 0;
    let resetStr: string = newMessage.content || '';
    const regex = /^<[^>]+>|^(.)/;
    const interval = language === 'zh' ? 40 : 28;
    let startTime = performance.now();
    const timeoutCallback = () => {
      try {
        // 1. 判断是新增消息还是拼接消息
        const type = ['start', 'complete'].includes(newMessage.status) && position === 0 ? 'ADD_LEFT_MESSAGE' : 'CONCAT_CONTENT';

        // 2. 切换到其他浏览器页签后, 直接拼接后端返回的文本, 无需前端分割
        // 如果是图片，不走流式直接整段直接返回，避免流式中，图片还没有加载完成
        if (splitStatusRef.current.visibilityState === 'hidden' || newMessage.content.includes('![db-chatbot-picture]')) {
          dispatch({
            type,
            payload: {
              ...newMessage,
              content: newMessage.content.slice(position, length),
              status: newMessage.status === 'complete' ? 'end' : newMessage.status
            }
          });
          resolve('');
          return;
        }

        // 3. 停止流式输出
        // if (!splitStatusRef.current.isStreaming) {
        // 	resolve("")
        // 	return;
        // }
        // 4. 调整帧率, 避免流式输出过快
        const currentTime = performance.now();
        const elapsedTime = currentTime - startTime;
        if (elapsedTime < interval) {
          splitStatusRef.current.animationFrameId = requestAnimationFrame(timeoutCallback);
          return;
        }
        startTime = currentTime;

        // 5. 获取本次分割的子字符串
        let subIndex = 0;
        // 获取页面更新的子字符串 (用正则获取 html元素的子字符串, 避免页面闪动)
        let subStr = resetStr[0] === '<' ? resetStr.match(regex)?.[0] || '' : '';
        subIndex += subStr?.length || 0;
        // 如果subStr没有值, 说明不是html元素, 需要遍历获取子字符串.
        // 遍历获取是为了跳过非汉字和字母的字符, 避免markdown转换html过程中解析不一致, 导致页面一闪一闪的问题
        if (!subStr) {
          do {
            if (resetStr[subIndex] === '<') break;
            subStr += resetStr[subIndex] || '';
            // eslint-disable-next-line no-plusplus
          } while (!isValidCharacter(resetStr[subIndex++]) && subIndex < resetStr.length);
        }
        position += subIndex;

        // 6. 当end状态时, 分割字符串, 只有最后一个字符的状态应该是end, 其他字符状态还应该是process
        const status = ['end', 'complete'].includes(newMessage.status)
          ? position >= length
            ? 'end'
            : 'process'
          : newMessage.status;

        // 7. 更新消息数据, 页面渲染新的字符
        dispatch({ type, payload: { ...newMessage, content: subStr, status } });
        // 8. 判断是否继续执行分割字符串.
        if (position >= length) {
          // splitStatusRef.current.isStreaming = false
          resolve('');
          splitStatusRef.current.remMessage = undefined;
          return;
        }
        resetStr = resetStr.slice(subIndex, length);
        // 记录剩余的content, 当浏览器页签切换的时候可以直接更新, 不进行流式输出
        splitStatusRef.current.remMessage = { ...newMessage, content: resetStr, status, type };
        splitStatusRef.current.animationFrameId = requestAnimationFrame(timeoutCallback);
      } catch (e) {
        console.error('前端分割content 错误', newMessage, e);
        // splitStatusRef.current.isStreaming = false;
        resolve(e);
      }
    };

    timeoutCallback();
  });

// 发送请求
function fetchTask(params: ChatRequestParams, options?: any) {
  const headers = new Headers();
  headers.append('Authorization', getAuthorization() || '');
  headers.append('Content-Type', 'application/json');
  const fetchUrl = params.url ?? '/api/v1/alg/chatbi/auto_analysis';
  return fetch(fetchUrl, {
    method: 'POST',
    body: JSON.stringify(omit(params, ['url'])),
    mode: 'cors',
    headers,
    cache: 'no-store',
    credentials: 'include',
    ...options
  });
}

// 请求消息
function fetchMessage(params: ChatRequestParams, readerRef, handleData) {
  const abortController = new AbortController();
  let timer: NodeJS.Timer;
  let lastValue = '';
  const decoder = new TextDecoder();
  fetchTask(params, { signal: abortController.signal })
    .then(response => {
      readerRef.current = response.body?.getReader();
      return new ReadableStream({
        start(controller) {
          function push() {
            // 设置超时时间
            timer = setTimeout(() => {
              console.error('任务请求超时');
              readerRef.current.cancel();
              abortController.abort();
              // 页面显示超时消息
              handleData({ code: 60001 });
              return;
            }, 120000);
            readerRef.current?.read().then(async ({ done, value }) => {
              clearTimeout(timer); // 清除上次的超时定时器
              if (done) {
                // console.log('流式输出完成');
                handleData(true);
                controller.close();
                lastValue = '';
                return;
              }
              const text = decoder.decode(value);
              // 后端要求在测试阶段输出的, 用于调试
              const jsons: any[] = [];

              function transformTextToJSON(text: string) {
                text?.split('<JSON>').forEach((json, index) => {
                  if (!json) return;
                  try {
                    // 第一个json字符串可能是上次接收的最后一个json字符串的剩余部分.
                    if (index === 0) {
                      json = lastValue + json;
                      lastValue = '';
                      // json 可能包含两个json字符串
                      json.includes('<JSON>') ? transformTextToJSON(json) : jsons.push(JSON.parse(json));
                    } else {
                      jsons.push(JSON.parse(json));
                    }
                  } catch (e) {
                    lastValue += json; // 最后一个json可能只有一半, 需要记录下来和下次的第一个json进行拼接
                  }
                });
              }

              transformTextToJSON(text);
              for (const json of jsons) {
                try {
                  await handleData(json);
                } catch (e) {
                  console.error('处理JSON错误', e);
                }
              }

              controller.enqueue(value);
              push();
            });
          }

          push();
        }
      });
    })
    .catch(e => {
      clearTimeout(timer);
      console.error('Chat接口Fetch请求错误: ', e);
    });
  return abortController;
}

// 查询计划消息
function getMessage(data, ...restProps) {
  return {
    ...data,
    isStreaming: true,
    question: restProps[2]
  };
}

// 存储结论消息数据
function getSummaryMessage(summary) {
  return {
    ...summary,
    type: 'summary',
    icon: 'circle-success',
    title: summary?.title,
    content: summary?.content || '',
    isStreaming: true
  };
}

// 存储错误消息
function getErrorMessage(error, dispatch) {
  // 后端返回的错误消息时, 就需要把之前的消息状态status改为end, 停止加载状态
  // 有时后端返回了错误消息后, 还没有断开连接, 还在继续发送数据, 所以这里只更改之前消息的status, 不更改任务分析状态(isComplete还是true)
  dispatch({ type: 'END_LAST_MESSAGE_STATUS' });
  return {
    ...error,
    type: 'error',
    state: error?.extra === 'pre_error_messgae' ? 'error' : 'warning',
    isStreaming: true
  };
}

// 存储图表消息
function getNewChartMessage({ extra }, dispatch, language) {
  const {
    menu_entity_type,
    menu_source,
    menu_metrics,
    legends,
    table_data,
    table_data_for_chart,
    chart_data,
    chart_sum_data,
    column_name,
    title,
    chart_url,
    file_info,
    chat_type,
    entity_type_relation,
    source_relation,
    metric_relation,
    ...restProps
  } = extra || {};

  const url = `${location.origin  }/api/v1/datalab${  file_info?.url}`;
  dispatch({ type: 'SAVE_CHART_INFO', payload: { title: title || '', url } });

  return {
    ...restProps,
    type: 'chart',
    chartIcon: 'circle-success',
    legends:
			legends && legends.length > 0
			  ? legends
			  : [{ compare_column: null, compare_name: '', data: null, icon: '', legend_column: null }],
    chartData: table_data_for_chart,
    pieData: table_data_for_chart,
    tableInfo: {
      data: table_data,
      columnsInfo: column_name || []
    },
    title: undefined,
    chartTitle: title || '',
    chartUrl: chart_url || '',
    fileInfo: {
      name: file_info?.name,
      url
    },
    chartTypes: chat_type.filter(type => type !== 'pie'),
    metrics: menu_metrics,
    entities: menu_entity_type,
    sources: menu_source,
    entityInfos: entity_type_relation,
    sourceInfos: source_relation,
    metricInfos: metric_relation,
    status: 'complete',
    isStreaming: false,
    language
  };
}

// 解析后端参数, 生成对应的messages
const messageTypeMap = {
  [MessageTypeEnum.PLANS]: getMessage,
  [MessageTypeEnum.CHART]: getNewChartMessage,
  [MessageTypeEnum.SUMMARY]: getSummaryMessage,
  [MessageTypeEnum.TEXT]: getMessage,
  [MessageTypeEnum.ERROR]: getErrorMessage
};

export default () => {
  const sessionIdRef = useRef('');
  const [state, dispatch] = useReducer(reducer, initialState);
  // 用于取消 fetch请求的读取流
  const readerRef = useRef<any>(null);
  // 用于取消fetch请求
  const controllerRef = useRef<any>(null);
  // 前端分割文本时的一些状态
  // isStreaming 是否处于流式输出中
  // visibilityState 页面是否是浏览器的当前页签.
  const splitStatusRef = useRef<any>({ isStreaming: false, visibilityState: 'visible' });
  // 后来者需要注意，使用这个hooks，返回的Promise是贯穿上下文的，也就是说只要run一遍，在组件的各个地方都能拿到最新返回值，把这个hooks放置在message的顶层，遵循数据自上而下流动
  const { runAsync, mutate } = useNextAskPromptExample();
  const dispatchEffect = useCallback((action: Action) => {
    switch (action.type) {
      case 'FETCH_MESSAGE': {
        const { gameCode, question, messages, language, domain, data, url } = action.payload;
        sessionIdRef.current = uid();
        const params = {
          session_id: sessionIdRef.current,
          content: question,
          stage: 'start',
          extra_info: {
            game_code: gameCode,
            language,
            messages, // 算法端要求上传问题和分析的结果.
            domain,
            data // 算法端要求上传的图表的标题和下载链接
          },
          url
        };

        if (!url) {
          runAsync(params);
        }
        controllerRef.current = fetchMessage(params, readerRef, response => 
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
					 new Promise<void>(async resolve => {
            // 消息处理
            if (response === true) {
              // 接收消息结束
              dispatch({ type: 'TASK_DONE' });
            } else if (response?.code != 0) {
              // 错误状态
              // 错误消息对象
              const timeoutError = {
                en: 'Error message: Interface request timeout, please try again later.',
                zh: '错误提示：接口请求超时，请稍后重试'
              };
              const systemError = {
                en: 'Error message: System error occurred.',
                zh: '错误提示：发生系统错误'
              };
              dispatch({ type: 'TASK_DONE' });
              dispatch({
                type: 'ADD_LEFT_MESSAGE',
                payload: {
                  type: 'error',
                  state: 'error',
                  content: response?.code == '60001' ? timeoutError[language] : systemError[language]
                }
              });
              // dispatch({type: "TASK_STATE", payload: true})
            } else {
              // 处理数据格式
              const data = response?.data || {};
              const { output = [], reply_id, language } = data;
              // 点赞点踩 配置项
              const feedbackLike = {
                type: 'feedback',
                handleFeedback(likeState: any) {
                  return updateLike({
                    like_state: likeState,
                    id: reply_id
                  });
                },
                hoverShow: true // 鼠标hover时才显示
              };
              // 更新消息数据

              for (const message of output) {
                console.log(message);
                const newMessage = messageTypeMap[message.type]?.(message, dispatch, language, question);
                if (!newMessage) continue;
                newMessage.feedbackOptions = feedbackLike;
                if (newMessage.isStreaming) {
                  await splitContent(newMessage, dispatch, splitStatusRef, data.language);
                } else {
                  dispatch({ type: 'ADD_LEFT_MESSAGE', payload: newMessage });
                }
              }
            }
            resolve();
          })
        );
        return;
      }
      case 'CANCEL_TASK': {
        // 发送取消任务的请求, 同时停止接收message
        // splitStatusRef.current.isStreaming = false // 如果正在打字过程, 则停止计时器
        mutate([]);
        window.cancelAnimationFrame(splitStatusRef.current.animationFrameId);
        readerRef.current?.cancel?.();
        controllerRef.current?.abort();
        fetchTask({ stage: 'stop', session_id: sessionIdRef.current });
        dispatch({ type: 'TASK_DONE' });
        return;
      }
      default: {
        dispatch(action);
      }
    }
  }, []);

  useEffect(() => {
    function visiblityCallback() {
      splitStatusRef.current.visibilityState = document.visibilityState;
      const { remMessage, resolve } = splitStatusRef.current;
      // 每次状态发生变化的时候，这里的Dom被hidden时，需要结合isComplete，配套使用
      if (document.visibilityState === 'hidden' && remMessage && !state.isComplete) {
        dispatch({ type: remMessage.type, payload: remMessage });
        resolve(''); // 停止分割字符的Promise
        // 在切换页签后, 停止当前正在进行的 requestAnimationFrame
        splitStatusRef.current.remMessage = undefined;
        window.cancelAnimationFrame(splitStatusRef.current.animationFrameId);
        // 停止流式输出
        // splitStatusRef.current.isStreaming = false
      } else {
        splitStatusRef.current.remMessage = undefined;
      }
    }
    document.addEventListener('visibilitychange', visiblityCallback);
    return () => {
      document.removeEventListener('visibilitychange', visiblityCallback);
    };
  }, [state.isComplete]);
  return { state, dispatch: dispatchEffect };
};
